import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Section } from "@/components/ui/Section";
import { Button, Typography } from "@/components/ui";

export interface DownloadButton {
  text: string;
  href: string;
  // Support legacy "outline" while aligning to new Button variants
  variant?: "primary" | "secondary" | "outlinePrimary" | "outlineSecondary" | "ghost" | "outline";
}

export interface StoreButton {
  text?: string;
  href: string;
  iconUrl: string;
  iconAlt: string;
}

export interface DownloadSectionProps {
  title: string;
  description?: string;
  downloadButtons: DownloadButton[];
  storeButton?: StoreButton;
  imageUrl: string;
  imageAlt: string;
  sectionClassName?: string;
  sectionBgColor?: string;
}

export function DownloadSection({
  title,
  description,
  downloadButtons,
  storeButton,
  imageUrl,
  imageAlt,
  sectionClassName,
  sectionBgColor,
}: DownloadSectionProps) {
  return (
    <Section variant="default" background="white" className={sectionClassName}>
      <div className={`container mx-auto px-4 py-9 ${sectionBgColor} rounded-[20px] shadow-[0px_3px_6px_rgba(0,0,0,.16)]`}>
        <div className="flex flex-col gap-8 md:gap-0 md:flex-row justify-between px-8 md:px-10">
          <div className="flex flex-col justify-center items-center md:items-start text-center md:text-left gap-5 w-full md:w-7/12">
            <Typography variant="h2" as="h2" className="font-medium text-[28px]">
              {title}
            </Typography>
            {description && (
              <Typography variant="body" as="p" className="text-gray-600">
                {description}
              </Typography>
            )}
            <div className="flex flex-col items-center w-full md:flex-wrap lg:flex-row gap-5 mt-4">
              {downloadButtons.map((button, index) => (
                <Link
                  key={index}
                  href={button.href}
                  target={button.href.startsWith("http") ? "_blank" : "_self"}
                  rel={button.href.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  <Button
                    variant={button.variant === "outline" ? "outlinePrimary" : (button.variant === "outlineSecondary" ? "outlineSecondary" : (button.variant || "primary"))}
                    size="md"
                  >
                    {button.text}
                  </Button>
                </Link>
              ))}

              {storeButton && (

                <Link
                  href={storeButton.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-xl border border-secondary btn-md inline-flex items-center justify-center"
                >
                  <Image
                    src={storeButton.iconUrl}
                    alt={storeButton.iconAlt}
                    title={storeButton.iconAlt}
                    loading="lazy"
                    decoding="async"
                    width={144}
                    height={44}
                    className="w-full h-6"
                    // style={{ width: "144px", height: "auto" }}
                  />
                </Link>
              )}
            </div>
          </div>
          <div className="w-full md:w-3/12">
            <Image
              src={imageUrl}
              alt={imageAlt}
              title={imageAlt}
              loading="lazy"
              decoding="async"
              width={500}
              height={200}
              className="w-full object-fill"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}
